import React, { type FC, memo } from 'react';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import type { PROMOTION_TYPES } from '@/domainLogic/index.ts';
import { useIconColorHex } from '@/app/common/icons/Icon';
import { starfishCssUrl } from '@/app/common/icons/starfish';
import type { StarfishIconRef } from '@/app/common/icons/starfish';
import { BIGGER_ICON_SIZE, ICON_SLOT, LabelWithIconContainer } from './styled';

type LabelWithIconProps = {
  icon?: StarfishIconRef | string;
  iconSize?: number;
  fontWeight?: string;
  labelHTML?: string;
  label?: string;
  className?: string;
  promotionType: PROMOTION_TYPES | null;
  callback?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
};

const LabelWithIcon: FC<LabelWithIconProps> = ({
  icon,
  iconSize,
  fontWeight,
  labelHTML,
  label,
  className,
  promotionType,
  callback,
}) => {
  const { localization } = usePageWrapperData();
  // The badge paints its icon as a background-image, so a named icon has to be
  // resolved to a URL here, where the theme is in reach.
  const namedIcon = typeof icon === 'object' ? icon : undefined;
  const hex = useIconColorHex(namedIcon?.color);
  const drawnSize = namedIcon?.inkScale ? Math.round(ICON_SLOT * namedIcon.inkScale) : iconSize;
  // With no size given, the promotion styles pick one per badge (up to the voucher-extra's
  // bigger icon), so the raster is requested at the largest of them.
  const iconUrl = namedIcon
    ? starfishCssUrl(namedIcon.category, namedIcon.name, hex, drawnSize ?? BIGGER_ICON_SIZE)
    : typeof icon === 'string'
      ? icon
      : undefined;

  return (
    <LabelWithIconContainer
      promotionType={promotionType}
      fontWeight={fontWeight}
      icon={iconUrl}
      iconSize={drawnSize}
      dangerouslySetInnerHTML={{ __html: label || labelHTML || '' }}
      localization={localization}
      {...(className ? { className } : {})}
      {...(callback ? { onClick: callback } : {})}
    />
  );
};

export default memo(LabelWithIcon);
